import { IMenuEditor, IMenuEditorAction } from 'models/menueditor/menueditor';
import { IMenuItemListItem } from 'models/menuitem/menuitemlistitem';
import { IStore } from 'redux/IStore';
import { IMenuItem } from 'models/menuitem/menuitem';
import { IKeyValuePair } from 'models/common';
import * as i18n from 'i18next';
import { actions as notifActions } from 'redux-notifications';
import { MenuItemType } from 'models/enums/menuitemtype';
import { getTreeFromFlatData, getFlatDataFromTree, TreeItem } from 'react-sortable-tree';
import { closeModal } from '../modal';
import * as c from 'models/constants';
import { IMenuType } from 'models/common/menutype';
const { notifSend } = notifActions;

/** Action Types */
export const REQUEST_START: string = 'menueditor/REQUEST_START';
export const REQUEST_FAILURE: string = 'menueditor/REQUEST_FAILURE';
export const GET_ITEMS_SUCCESS: string = 'menueditor/GET_ITEMS_SUCCESS';
export const GET_MENU_ITEMS_SUCCESS: string = 'menueditor/GET_MENU_ITEMS_SUCCESS';
export const RESET_ALL_DATA: string = 'menueditor/RESET_ALL_DATA';
export const RESET_MENU_ITEMS_DATA: string = 'menueditor/RESET_MENU_ITEMS_DATA';
export const SET_SELECTED_LANGUAGE: string = 'menueditor/SET_SELECTED_LANGUAGE';

/** Initial State */
const initialState: IMenuEditor = {
	items: [],
	menuItems: [],
	selectedLanguageKey: null,
};

/** Reducer */
export function menuEditorReducer(state = initialState, action: IMenuEditorAction) {
	switch (action.type) {
		case REQUEST_START:
			return Object.assign({}, state, {
				isFetching: true,
				error: false,
			});

		case REQUEST_FAILURE:
			return Object.assign({}, state, {
				isFetching: false,
				message: action.payload.message,
				error: true,
			});

		case GET_ITEMS_SUCCESS:
			return Object.assign({}, state, {
				items: action.payload.items,
			});

		case GET_MENU_ITEMS_SUCCESS:
			return Object.assign({}, state, {
				menuItems: action.payload.menuItems,
				isFetching: false,
			});

		case RESET_ALL_DATA:
			return Object.assign({}, state, {
				menuItems: [],
				menuItemsPage: {
					currentPage: 0,
					totalPages: 0,
					totalItems: 0,
					itemsPerPage: 40,
					items: [],
				},
				items: [],
				selectedLanguageKey: null,
			});

		case RESET_MENU_ITEMS_DATA:
			return Object.assign({}, state, {
				menuItems: [],
			});

		case SET_SELECTED_LANGUAGE:
			return Object.assign({}, state, {
				selectedLanguageKey: action.payload.selectedLanguageKey,
			});

		default:
			return state;
	}
}

/** Action Creator */
export function getEditorItems(menuItemType: MenuItemType) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		let url = 'api/MenuItem/';

		if (menuItemType === MenuItemType.Portal) {
			url = url + 'Portal/Published';
		} else if (menuItemType === MenuItemType.Magazine) {
			url = url + 'Magazine/Published';
		}

		const language = getState().menuEditor.selectedLanguageKey;

		if (language) {
			url += '?language=' + language;
		}

		return fetch(getState().fetchDefault.baseAPI + url, Object.assign({}, getState().fetchDefault.requestInit))
			.then((res) => {
				if (res.ok) {
					return res.json().then((res: IMenuItemListItem[]) => {
						res.map((item: IMenuItemListItem) => {
							item.pageJObject = JSON.parse(item.page);
						});

						res.sort((a, b) => a.sortOrder - b.sortOrder);

						const flatData = res.map((item: IMenuItemListItem) => {
							const itemToAdd: any = {
								id: item.id,
								parent: item.parentKey,
								children: [],
							};

							if (
								(item.pageJObject as IMenuType).menuType === c.MENU_TYPE_PAGE ||
								(item.pageJObject as IMenuType).menuType === c.MENU_TYPE_PORTAL_LINK ||
								(item.pageJObject as IMenuType).menuType === c.MENU_TYPE_MAGAZINE_LINK
							) {
								itemToAdd.name = item.title;
								itemToAdd.title = item.title;
							} else if ((item.pageJObject as IMenuType).menuType === c.MENU_TYPE_CATEGORY) {
								itemToAdd.name = i18n.t(`DV_Categories_${item.title}`);
								itemToAdd.title = i18n.t(`DV_Categories_${item.title}`);
							}

							return itemToAdd;
						});

						const editorItems = getTreeFromFlatData<IMenuItem>({
							flatData: flatData.map((node) => ({ ...node, title: node.name })),
							getKey: (node) => node.id, // resolve a node's key
							getParentKey: (node) => node.parent, // resolve a node's parent's key
							rootKey: null, // The value of the parent key when there is no parent (i.e., at root level)
						});

						dispatch(getItemsSuccess(editorItems));
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function getMenuItems(menuItemType: MenuItemType) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		let url = getState().fetchDefault.baseAPI + 'api/MenuItem/';

		if (menuItemType === MenuItemType.Portal) {
			url = url + 'Portal/Search';
		} else if (menuItemType === MenuItemType.Magazine) {
			url = url + 'Magazine/Search';
		}

		let queryString = '?';
		queryString = queryString + '&languageKey=' + getState().menuEditor.selectedLanguageKey;
		queryString = queryString + '&currentPage=1';
		queryString = queryString + `&itemsPerPage=${Number.MAX_SAFE_INTEGER}`;

		return fetch(
			url + queryString,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'GET',
			}),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((res) => {
						res.items.map((item) => {
							item.pageJObject = JSON.parse(item.page);
							item.source =
								menuItemType === MenuItemType.Magazine ? c.MENU_TYPE_MAGAZINE_LINK : c.MENU_TYPE_PORTAL_LINK;
						});
						const items = res.items;
						let url = getState().fetchDefault.baseAPI + 'api/MenuItem/';

						if (menuItemType === MenuItemType.Portal) {
							url = url + 'Magazine/Search';
						} else if (menuItemType === MenuItemType.Magazine) {
							url = url + 'Portal/Search';
						}

						let queryString = '?';
						queryString = queryString + '&languageKey=' + getState().menuEditor.selectedLanguageKey;
						queryString = queryString + '&currentPage=1';
						queryString = queryString + `&itemsPerPage=${Number.MAX_SAFE_INTEGER}`;

						return fetch(
							url + queryString,
							Object.assign({}, getState().fetchDefault.requestInit, {
								method: 'GET',
							}),
						)
							.then((res) => {
								if (res.ok) {
									return res.json().then((res) => {
										res.items.map((item) => {
											item.pageJObject = JSON.parse(item.page);
											item.source =
												menuItemType === MenuItemType.Magazine ? c.MENU_TYPE_PORTAL_LINK : c.MENU_TYPE_MAGAZINE_LINK;
										});
										const all = items.concat(res.items);
										dispatch(getMenuItemsSuccess(all));
									});
								} else {
									const status: number = res.status;
									const statusText: string = res.statusText;
									return res
										.json()
										.then((res) => {
											res.status = status;
											res.statusText = statusText;
											dispatch(requestFailure(res));
										})
										.catch(() => {
											const message: any = { status, statusText };
											dispatch(requestFailure(message));
										});
								}
							})
							.catch((err) => {
								dispatch(requestFailure(err));
							});
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator - create */
export function createCategoryMenuItem(categoryKey: number, menuItemType: MenuItemType) {
	return (dispatch, getState: () => IStore) => {
		let url: string = getState().fetchDefault.baseAPI + 'api/MenuItem/';

		if (menuItemType === MenuItemType.Portal) {
			url = url + 'Portal';
		} else if (menuItemType === MenuItemType.Magazine) {
			url = url + 'Magazine';
		}

		dispatch(requestStart());

		const pageObject: IMenuType = {
			menuType: c.MENU_TYPE_CATEGORY,
		};

		const menuItem: IMenuItem = {
			title: categoryKey.toString(),
			code: new Date().getTime().toString(),
			page: JSON.stringify(pageObject),
			languageKey: parseInt(getState().menuEditor.selectedLanguageKey, 10),
		};

		return fetch(
			url,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'POST',
				body: JSON.stringify(menuItem),
			}),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((newMenuItem: IMenuItem) => {
						const menuItems = getState().menuEditor.menuItems;
						menuItems.push(newMenuItem);
						dispatch(getMenuItemsSuccess(menuItems));
						dispatch(addNewItemToMenu(newMenuItem.id));
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator - create */
export function createLinkMenuItem(title: string, link: string, menuItemType: MenuItemType, menuType: string) {
	return (dispatch, getState: () => IStore) => {
		let url: string = getState().fetchDefault.baseAPI + 'api/MenuItem/';

		if (menuItemType === MenuItemType.Portal) {
			url = url + 'Portal';
		} else if (menuItemType === MenuItemType.Magazine) {
			url = url + 'Magazine';
		}

		dispatch(requestStart());

		const pageObject: IMenuType = {
			menuType,
		};

		const menuItem: IMenuItem = {
			title,
			description: link, //hacky fill
			code: new Date().getTime().toString(),
			page: JSON.stringify(pageObject),
			languageKey: parseInt(getState().menuEditor.selectedLanguageKey, 10),
		};

		return fetch(
			url,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'POST',
				body: JSON.stringify(menuItem),
			}),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((newMenuItem: IMenuItem) => {
						const menuItems = getState().menuEditor.menuItems;
						menuItems.push(newMenuItem);
						dispatch(getMenuItemsSuccess(menuItems));
						dispatch(addNewItemToMenu(newMenuItem.id));
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

export function setSortOrderToItems(items: TreeItem<IMenuItem>[]) {
	items.forEach((item, index) => {
		item.sortOrder = index;
		if (item.children && item.children.length > 0) {
			setSortOrderToItems(item.children as TreeItem<IMenuItem>[]);
		}
	});
}

/** Action Creator */
export function publishMenu(menuItemType: MenuItemType) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		const items: TreeItem<IMenuItem>[] = getState().menuEditor.items;
		setSortOrderToItems(items);

		const flatData = getFlatDataFromTree({
			treeData: items,
			getNodeKey: ({ node }) => node.id,
			ignoreCollapsed: false,
		}).map(({ node, path }) => ({
			id: node.id,
			name: node.name,
			parent: path.length > 1 ? path[path.length - 2] : null,
			sortOrder: node.sortOrder,
		}));

		const itemsToPublish: IKeyValuePair[] = flatData.map((item) => {
			const itemToPublish: IKeyValuePair = {
				key: item.id,
				value: {
					Item1: item.parent,
					Item2: item.sortOrder,
				},
			};
			return itemToPublish;
		});

		const selectedLanguageKey = getState().menuEditor.selectedLanguageKey;

		const data = {
			Item1: itemsToPublish,
			Item2: selectedLanguageKey,
		};

		return fetch(
			getState().fetchDefault.baseAPI + 'api/MenuItem/Publish',
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'POST',
				body: JSON.stringify(data),
			}),
		)
			.then((res) => {
				if (res.ok) {
					if (menuItemType === MenuItemType.Portal) {
						dispatch(
							notifSend({
								message: i18n.t('Notification_Success_Portal_Menu_Published'),
								kind: 'success',
								dismissAfter: 3000,
							}),
						);
					} else if (menuItemType === MenuItemType.Magazine) {
						dispatch(
							notifSend({
								message: i18n.t('Notification_Success_Magazine_Menu_Published'),
								kind: 'success',
								dismissAfter: 3000,
							}),
						);
					}
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function selectLanguageAndLoadEditorMenuItems(selectedLanguageKey: number, menuItemType: MenuItemType) {
	return (dispatch) => {
		dispatch(setSelectedLanguageKey(selectedLanguageKey));
		dispatch(getEditorItems(menuItemType));
	};
}

/** Action Creator */
export function addNewItemToMenu(id: string) {
	return (dispatch, getState: () => IStore) => {
		const menuEditorState = getState().menuEditor;
		const menuItem = menuEditorState.menuItems.find((x) => x.id === id);
		let items = menuEditorState.items;

		const item: TreeItem<IMenuItem> = {
			id: menuItem.id,
			parent: null,
			children: [],
		};

		const menuType = JSON.parse(menuItem.page).menuType;
		if (
			menuType === c.MENU_TYPE_PAGE ||
			menuType === c.MENU_TYPE_PORTAL_LINK ||
			menuType === c.MENU_TYPE_MAGAZINE_LINK
		) {
			item.name = menuItem.title;
			item.title = menuItem.title;
		} else if (menuType === c.MENU_TYPE_CATEGORY) {
			item.name = i18n.t(`DV_Categories_${menuItem.title}`);
			item.title = i18n.t(`DV_Categories_${menuItem.title}`);
		}

		items = items.concat([item]);

		dispatch(getItemsSuccess(items));
		dispatch(closeModal());
	};
}

/** Action Creator */
export function setSelectedLanguageKey(selectedLanguageKey: number): IMenuEditorAction {
	return {
		type: SET_SELECTED_LANGUAGE,
		payload: {
			selectedLanguageKey,
		},
	};
}

/** Action Creator */
export function updateItemsOrder(items: TreeItem[]) {
	return (dispatch) => {
		dispatch(getItemsSuccess(items));
	};
}

/** Action Creator */
export function requestStart(): IMenuEditorAction {
	return {
		type: REQUEST_START,
	};
}

/** Action Creator */
export function requestFailure(message: any): IMenuEditorAction {
	return {
		type: REQUEST_FAILURE,
		payload: {
			message,
		},
	};
}

/** Action Creator */
export function getItemsSuccess(items: TreeItem[]): IMenuEditorAction {
	return {
		type: GET_ITEMS_SUCCESS,
		payload: {
			items,
		},
	};
}

/** Action Creator */
export function getMenuItemsSuccess(menuItems: IMenuItem[]): IMenuEditorAction {
	return {
		type: GET_MENU_ITEMS_SUCCESS,
		payload: {
			menuItems,
		},
	};
}

/** Action Creator */
export function resetAllData(): IMenuEditorAction {
	return {
		type: RESET_ALL_DATA,
	};
}

/** Action Creator */
export function resetMenuItemsData(): IMenuEditorAction {
	return {
		type: RESET_MENU_ITEMS_DATA,
	};
}
